import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../lib/firebase';
import { 
  collection, addDoc, serverTimestamp, query, where, 
  getDocs, orderBy, onSnapshot, doc, updateDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  LifeBuoy, Send, MessageSquare, Wrench, CreditCard, 
  Tag, ArrowLeft, Loader2, ShieldCheck, Camera,
  Clock, CheckCircle2, AlertCircle, FileText, X, 
  Image as ImageIcon, HelpCircle, UserCog, Ban, Plus, Mail, Reply, ArrowRight, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from "../components/ui/dialog";
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import Navbar from '../components/ui/Navbar';
import planoFundo from "../assets/planodefundo.png"; 

export default function Support() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isTech, setIsTech] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null); 
  const [ticketMessages, setTicketMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  
  // Imagens para novo chamado
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Imagens para réplica
  const [replyImages, setReplyImages] = useState([]);
  const [replyPreviews, setReplyPreviews] = useState([]);

  const [formData, setFormData] = useState({
    category: '',
    orderId: location.state?.orderId || '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    if (userProfile?.userType === 'technician') setIsTech(true);
  }, [userProfile]);

  useEffect(() => {
    if (!user?.uid) return;

    const fetchOrders = async () => {
      try {
        const field = userProfile?.userType === 'technician' ? 'technicianId' : 'clientId';
        const q = query(collection(db, 'orders'), where(field, '==', user.uid), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error("Erro OS:", e); }
    };

    const qTickets = query(
      collection(db, 'support_tickets'), 
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubTickets = onSnapshot(qTickets, (snap) => {
      setMyTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      const fallbackQuery = query(collection(db, 'support_tickets'), where('userId', '==', user.uid));
      onSnapshot(fallbackQuery, (snap) => setMyTickets(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    });

    fetchOrders();
    return () => unsubTickets();
  }, [user, userProfile]);

  useEffect(() => {
    if (!selectedTicket) return;
    const qMsgs = query(collection(db, 'support_tickets', selectedTicket.id, 'messages'), orderBy('createdAt', 'asc'));
    return onSnapshot(qMsgs, (snap) => {
      setTicketMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [selectedTicket]);

  const filteredTickets = myTickets.filter(t => activeFilter === 'all' ? true : t.status === activeFilter);

  const createNotification = async (targetId, title, msg, type) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId: targetId,
        title,
        message: msg,
        type,
        category: 'support',
        read: false,
        createdAt: serverTimestamp()
      });
    } catch (e) { console.error(e); }
  };

  const handleReplyImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (replyImages.length + files.length > 3) return toast.error("Máximo 3 fotos na réplica.");
    files.forEach(file => {
      setReplyImages(prev => [...prev, file]);
      setReplyPreviews(prev => [...prev, URL.createObjectURL(file)]);
    });
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() && replyImages.length === 0) return;
    setLoading(true);
    
    try {
      const replyAttachmentUrls = [];
      for (const file of replyImages) {
        const storageRef = ref(storage, `support_attachments/${user.uid}_reply_${Date.now()}_${Math.random().toString(36).substring(7)}`);
        const uploadTask = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(uploadTask.ref);
        replyAttachmentUrls.push(url);
      }

      await addDoc(collection(db, 'support_tickets', selectedTicket.id, 'messages'), {
        text: replyText,
        senderId: user.uid,
        senderName: userProfile.name,
        senderType: 'user',
        attachmentUrls: replyAttachmentUrls,
        createdAt: serverTimestamp()
      });

      await updateDoc(doc(db, 'support_tickets', selectedTicket.id), { 
        lastMessageAt: serverTimestamp(),
        status: 'pending' 
      });
      
      await createNotification('admin_central', "Réplica no Chamado", `Protocolo #${selectedTicket.protocol}`, 'admin');
      
      setReplyText("");
      setReplyImages([]);
      setReplyPreviews([]);
      toast.success("Resposta enviada!");
    } catch (e) { toast.error("Erro ao responder."); } finally { setLoading(false); }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    const protocol = `SUP${Math.floor(100000 + Math.random() * 900000)}`;
    const attachmentUrls = [];
    try {
      for (const file of selectedImages) {
        const storageRef = ref(storage, `support_attachments/${user.uid}_${Date.now()}_${Math.random().toString(36).substring(7)}`);
        const uploadTask = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(uploadTask.ref);
        attachmentUrls.push(url);
      }
      const ticketRef = await addDoc(collection(db, 'support_tickets'), {
        userId: user.uid,
        userName: userProfile.name,
        userEmail: userProfile.email,
        userType: userProfile.userType,
        category: formData.category,
        orderId: formData.orderId || null,
        subject: formData.subject,
        attachmentUrls,
        status: 'pending',
        protocol,
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp()
      });

      await addDoc(collection(db, 'support_tickets', ticketRef.id, 'messages'), {
        text: formData.message,
        senderId: user.uid,
        senderName: userProfile.name,
        senderType: 'user',
        createdAt: serverTimestamp()
      });

      await createNotification('admin_central', "Novo Chamado", `Protocolo #${protocol}`, 'admin');

      toast.success("Enviado com sucesso!");
      setFormData({ category: '', orderId: '', subject: '', message: '' });
      setSelectedImages([]); setImagePreviews([]); setShowConfirmModal(false);
    } catch (error) { toast.error("Erro ao abrir."); } finally { setLoading(false); }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-600 border-amber-200",
      responded: "bg-blue-100 text-blue-600 border-blue-200",
      completed: "bg-green-100 text-green-600 border-green-200"
    };
    const labels = { pending: "ABERTO", responded: "RESPONDIDO", completed: "CONCLUÍDO" };
    return <Badge className={`${styles[status]} font-black italic text-[8px] px-3 py-1 border rounded-full`}>{labels[status]}</Badge>;
  };

  return (
    <div className="relative min-h-screen font-sans text-slate-900 text-left overflow-x-hidden bg-[#f8fafc]">
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.06]" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover' }} />
      <Navbar />

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-20 text-left">
        <header className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6 text-left">
            <div className="text-left">
                <h1 className="text-5xl font-black uppercase italic tracking-tighter text-slate-900 leading-none">Suporte <span className={isTech ? 'text-orange-600' : 'text-blue-600'}>Socorre Aí</span></h1>
                <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.3em] mt-3 flex items-center gap-2 italic">
                  <Mail size={14} className="text-blue-600" /> Atendimento Profissional por Chamados
                </p>
            </div>
            <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-full font-black text-[10px] uppercase italic text-slate-400 border border-slate-200 h-12 px-8">
                <ArrowLeft size={16} /> Voltar
            </Button>
        </header>

        <Tabs defaultValue="new" className="w-full text-left">
            <TabsList className="bg-white/80 backdrop-blur-md p-1.5 rounded-[2rem] border border-slate-200 mb-10 shadow-lg">
                <TabsTrigger value="new" className="rounded-[1.6rem] font-black uppercase italic text-[10px] px-10 py-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white">Novo Chamado</TabsTrigger>
                <TabsTrigger value="history" className="rounded-[1.6rem] font-black uppercase italic text-[10px] px-10 py-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white">Meus Chamados ({myTickets.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="new" className="animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
                <div className="bg-white rounded-[3rem] shadow-2xl border border-slate-100 p-10 md:p-14 text-left">
                    <form onSubmit={(e) => { e.preventDefault(); setShowConfirmModal(true); }} className="space-y-10 text-left">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            <div className="space-y-3">
                                <Label className="text-[11px] font-black uppercase text-slate-400 ml-4 italic">Departamento</Label>
                                <Select onValueChange={(v) => setFormData({...formData, category: v})} value={formData.category} required>
                                    <SelectTrigger className="h-16 rounded-[1.8rem] bg-slate-50 border-none font-bold uppercase italic shadow-inner px-8">
                                        <SelectValue placeholder="Selecione o motivo..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white rounded-2xl border-none shadow-2xl">
                                        <SelectItem value="os_issue" className="py-4 font-black uppercase italic text-[10px]">⚠️ Problema em Serviço (OS)</SelectItem>
                                        <SelectItem value="payment" className="py-4 font-black uppercase italic text-[10px]">💳 Financeiro / Cobrança</SelectItem>
                                        <SelectItem value="account" className="py-4 font-black uppercase italic text-[10px]">👤 Conta / Acesso</SelectItem>
                                        <SelectItem value="report" className="py-4 font-black uppercase italic text-[10px]">🚫 Denunciar Conduta</SelectItem>
                                        <SelectItem value="suggestion" className="py-4 font-black uppercase italic text-[10px]">💡 Sugestões e Idéias</SelectItem>
                                        <SelectItem value="general" className="py-4 font-black uppercase italic text-[10px]">❓ Outros Assuntos</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-3">
                                <Label className="text-[11px] font-black uppercase text-slate-400 ml-4 italic">Assunto</Label>
                                <Input placeholder="TÍTULO DA MENSAGEM..." className="h-16 rounded-[1.8rem] bg-slate-50 border-none font-black uppercase italic shadow-inner px-8" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value.toUpperCase()})} required />
                            </div>
                        </div>

                        {(formData.category === 'os_issue' || formData.category === 'payment') && (
                            <div className="space-y-3 animate-in slide-in-from-top-4 text-left">
                                <Label className="text-[11px] font-black uppercase text-blue-600 ml-4 italic">Vincular Ordem de Serviço</Label>
                                <Select onValueChange={(v) => setFormData({...formData, orderId: v})} value={formData.orderId} required>
                                    <SelectTrigger className="h-16 rounded-[1.8rem] bg-blue-50/50 border-2 border-blue-100 font-black uppercase italic px-8">
                                        <SelectValue placeholder="Selecione a OS correspondente..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white rounded-2xl max-h-72">
                                        {orders.map(o => (
                                            <SelectItem key={o.id} value={o.id} className="font-black uppercase italic text-[10px] py-4">#{o.serviceCode} - {o.serviceArea}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-3">
                            <Label className="text-[11px] font-black uppercase text-slate-400 ml-4 italic">Mensagem</Label>
                            <Textarea placeholder="Descreva aqui sua solicitação..." className="min-h-[180px] rounded-[2.5rem] bg-slate-50 border-none font-bold p-8 shadow-inner leading-relaxed resize-none text-left" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required />
                        </div>

                        <div className="flex flex-col md:flex-row items-center gap-10">
                            <div className="w-full md:w-auto space-y-3 text-left">
                                <Label className="text-[11px] font-black uppercase text-slate-400 ml-4 italic">Fotos do Problema ({selectedImages.length}/5)</Label>
                                <div className="flex flex-wrap gap-4">
                                    {selectedImages.length < 5 && (
                                        <button type="button" onClick={() => fileInputRef.current.click()} className="w-20 h-20 rounded-[1.5rem] bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:bg-slate-200 transition-all shadow-inner">
                                            <Plus size={24} />
                                            <input type="file" multiple className="hidden" ref={fileInputRef} onChange={(e) => {
                                                const files = Array.from(e.target.files);
                                                setSelectedImages(prev => [...prev, ...files]);
                                                setImagePreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
                                            }} accept="image/*" />
                                        </button>
                                    )}
                                    {imagePreviews.map((url, i) => (
                                        <div key={i} className="relative w-20 h-20 rounded-[1.5rem] overflow-hidden border-4 border-white shadow-xl group">
                                            <img src={url} className="w-full h-full object-cover" />
                                            <button type="button" onClick={() => {
                                                setSelectedImages(prev => prev.filter((_, idx) => idx !== i));
                                                setImagePreviews(prev => prev.filter((_, idx) => idx !== i));
                                            }} className="absolute inset-0 bg-red-600/90 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all"><X size={20}/></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <Button type="submit" disabled={loading} className={`flex-1 h-20 rounded-[2.2rem] font-black uppercase italic shadow-2xl text-white text-lg transition-all ${isTech ? 'bg-orange-600' : 'bg-blue-600'}`}>
                                {loading ? <Loader2 className="animate-spin" /> : 'ENVIAR PARA O SUPORTE'}
                            </Button>
                        </div>
                    </form>
                </div>
            </TabsContent>

            <TabsContent value="history" className="animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
                <div className="flex flex-wrap gap-2 mb-8 bg-white/50 p-2 rounded-3xl w-fit border border-slate-100 shadow-sm text-left">
                    <button onClick={() => setActiveFilter('all')} className={`px-6 py-2 rounded-2xl text-[9px] font-black uppercase italic transition-all ${activeFilter === 'all' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-white'}`}>Todos</button>
                    <button onClick={() => setActiveFilter('pending')} className={`px-6 py-2 rounded-2xl text-[9px] font-black uppercase italic transition-all ${activeFilter === 'pending' ? 'bg-amber-500 text-white shadow-lg' : 'text-amber-500 hover:bg-amber-50'}`}>Abertos</button>
                    <button onClick={() => setActiveFilter('responded')} className={`px-6 py-2 rounded-2xl text-[9px] font-black uppercase italic transition-all ${activeFilter === 'responded' ? 'bg-blue-600 text-white shadow-lg' : 'text-blue-500 hover:bg-blue-50'}`}>Respondidos</button>
                    <button onClick={() => setActiveFilter('completed')} className={`px-6 py-2 rounded-2xl text-[9px] font-black uppercase italic transition-all ${activeFilter === 'completed' ? 'bg-green-600 text-white shadow-lg' : 'text-green-500 hover:bg-green-50'}`}>Concluídos</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
                    {filteredTickets.map(ticket => (
                        <div key={ticket.id} onClick={() => setSelectedTicket(ticket)} className={`bg-white rounded-[2.5rem] p-8 shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all cursor-pointer border-2 ${ticket.status === 'responded' ? 'border-blue-100 bg-blue-50/5' : 'border-transparent'} hover:border-blue-500 group text-left`}>
                            <div className="flex items-center gap-2 mb-4">
                                <span className="text-orange-500 font-black text-[10px] italic">#{ticket.protocol}</span>
                                {getStatusBadge(ticket.status)}
                            </div>
                            <h4 className="text-slate-900 font-black uppercase italic text-lg leading-tight mb-4">{ticket.subject}</h4>
                            <div className="flex items-center justify-between text-[10px] font-black text-slate-400 uppercase italic border-t border-slate-50 pt-4">
                                <div className="flex items-center gap-1.5"><Clock size={12} /> {ticket.createdAt?.toDate ? ticket.createdAt.toDate().toLocaleDateString('pt-BR') : 'Agora'}</div>
                                <div className="text-blue-600 flex items-center gap-1 font-black uppercase">Ver Mensagens <ArrowRight size={12}/></div>
                            </div>
                        </div>
                    ))}
                </div>
            </TabsContent>
        </Tabs>
      </main>

      {/* MODAL DE HISTÓRICO VISUAL DE E-MAIL COM OPÇÃO DE FOTO NA RÉPLICA */}
      <Dialog open={!!selectedTicket} onOpenChange={() => { setSelectedTicket(null); setReplyImages([]); setReplyPreviews([]); }}>
        <DialogContent className="rounded-[3rem] border-none p-0 bg-slate-50 max-w-4xl h-[85vh] overflow-hidden shadow-2xl flex flex-col text-left">
          {selectedTicket && (
            <>
                <div className={`p-8 text-white flex items-center justify-between shrink-0 ${isTech ? 'bg-orange-600' : 'bg-blue-600'}`}>
                    <div className="flex items-center gap-4 text-left">
                        <Mail size={28} />
                        <div className="text-left">
                            <h2 className="text-xl font-black uppercase italic tracking-tighter text-white">Mensagens do Ticket #{selectedTicket.protocol}</h2>
                            <p className="text-[10px] font-black uppercase italic opacity-80 mt-1 text-white">{selectedTicket.subject}</p>
                        </div>
                    </div>
                    <Button variant="ghost" onClick={() => setSelectedTicket(null)} className="text-white hover:bg-white/10 rounded-full h-10 w-10 p-0"><X size={20}/></Button>
                </div>

                <div className="flex-1 overflow-y-auto p-10 space-y-10 bg-slate-50 text-left no-scrollbar">
                    {ticketMessages.map((msg, i) => {
                        const isSupport = msg.senderType === 'admin';
                        return (
                            <div key={i} className="animate-in fade-in slide-in-from-bottom-4 duration-300 text-left">
                                <div className={`p-8 rounded-[2rem] shadow-sm border ${isSupport ? 'bg-blue-600 text-white border-blue-500 shadow-blue-200' : 'bg-white text-slate-800 border-slate-100'} text-left`}>
                                    <div className="flex items-center justify-between mb-6 border-b pb-4 opacity-80 text-left">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isSupport ? 'bg-white/20' : 'bg-slate-100'}`}>
                                                <User size={14} className={isSupport ? 'text-white' : 'text-slate-500'} />
                                            </div>
                                            <span className="text-[10px] font-black uppercase italic">{isSupport ? 'Suporte Oficial' : msg.senderName}</span>
                                        </div>
                                        <span className="text-[9px] font-black uppercase italic">{msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleString('pt-BR') : 'Enviando...'}</span>
                                    </div>
                                    <p className="text-sm font-bold italic leading-relaxed text-left whitespace-pre-wrap">"{msg.text}"</p>
                                    
                                    {/* Exibição de anexos em qualquer mensagem que possua */}
                                    {msg.attachmentUrls?.length > 0 && (
                                        <div className="mt-8 pt-6 border-t border-slate-100/20 text-left">
                                            <p className={`text-[9px] font-black uppercase italic mb-3 ${isSupport ? 'text-white' : 'text-slate-400'}`}>Mídia do Chamado:</p>
                                            <div className="flex flex-wrap gap-3">
                                                {msg.attachmentUrls.map((url, idx) => (
                                                    <a key={idx} href={url} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-xl overflow-hidden border-2 border-white shadow-sm">
                                                        <img src={url} className="w-full h-full object-cover" />
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Caso seja a primeira mensagem e tenha fotos globais do ticket */}
                                    {i === 0 && selectedTicket.attachmentUrls?.length > 0 && !msg.attachmentUrls && (
                                        <div className="mt-8 pt-6 border-t border-slate-100/20 text-left">
                                            <p className="text-[9px] font-black uppercase italic mb-3 text-slate-400">Anexos Iniciais:</p>
                                            <div className="flex flex-wrap gap-3">
                                                {selectedTicket.attachmentUrls.map((url, idx) => (
                                                    <a key={idx} href={url} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-xl overflow-hidden border-2 border-white shadow-sm">
                                                        <img src={url} className="w-full h-full object-cover" />
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {selectedTicket.status !== 'completed' && (
                  <div className="p-8 bg-white border-t border-slate-100 shrink-0 text-left">
                      <div className="space-y-4 text-left">
                          <div className="flex items-center justify-between px-4">
                              <Label className="text-[9px] font-black uppercase text-slate-400 italic">Redigir Réplica</Label>
                              <div className="flex items-center gap-2">
                                <input type="file" multiple className="hidden" ref={replyFileInputRef} onChange={handleReplyImageChange} accept="image/*" />
                                <Button type="button" onClick={() => replyFileInputRef.current.click()} variant="ghost" className="h-10 text-[9px] font-black uppercase italic gap-2 text-blue-600 hover:bg-blue-50 px-4 rounded-xl border border-blue-100">
                                    <Camera size={14} /> Anexar Prova ({replyImages.length}/3)
                                </Button>
                              </div>
                          </div>

                          <div className="flex gap-4 items-start text-left">
                            <div className="flex-1 space-y-3">
                                <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="DESCREVA SUA RESPOSTA..." className="h-24 rounded-2xl bg-slate-50 border-none font-black uppercase italic px-8 py-5 shadow-inner text-left resize-none" />
                                
                                {replyPreviews.length > 0 && (
                                    <div className="flex flex-wrap gap-2 animate-in zoom-in-75">
                                        {replyPreviews.map((url, i) => (
                                            <div key={i} className="relative w-12 h-12 rounded-xl overflow-hidden border-2 border-white shadow-lg group">
                                                <img src={url} className="w-full h-full object-cover" />
                                                <button type="button" onClick={() => {
                                                    setReplyImages(prev => prev.filter((_, idx) => idx !== i));
                                                    setReplyPreviews(prev => prev.filter((_, idx) => idx !== i));
                                                }} className="absolute inset-0 bg-red-600/90 text-white opacity-0 hover:opacity-100 flex items-center justify-center transition-all"><X size={14}/></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Button onClick={handleSendReply} disabled={loading || (!replyText.trim() && replyImages.length === 0)} className={`h-24 w-24 rounded-[2rem] text-white shadow-xl flex flex-col items-center justify-center gap-1 ${isTech ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                                {loading ? <Loader2 className="animate-spin" /> : <><Reply size={24} /> <span className="text-[8px] font-black uppercase">Enviar</span></>}
                            </Button>
                          </div>
                      </div>
                  </div>
                )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* MODAL DE CONFIRMAÇÃO */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="rounded-[3rem] border-none p-12 bg-white max-w-xl text-left shadow-2xl">
          <DialogHeader className="text-left">
            <DialogTitle className="text-3xl font-black uppercase italic tracking-tighter text-slate-900 flex items-center gap-3"><Mail className="text-blue-600" /> Confirmar Ticket</DialogTitle>
          </DialogHeader>
          <div className="my-8 bg-slate-50 p-8 rounded-[2rem] border border-slate-100 space-y-4">
             <div><Label className="text-[10px] font-black text-slate-400 uppercase italic ml-2">Assunto</Label><p className="font-black text-slate-900 uppercase italic text-lg px-2 text-left">{formData.subject}</p></div>
             <div><Label className="text-[10px] font-black text-slate-400 uppercase italic ml-2">Fila de Atendimento</Label><p className="font-black text-blue-600 uppercase italic text-sm px-2 text-left">Suporte Técnico Socorre Aí</p></div>
          </div>
          <DialogFooter className="flex gap-4 text-left">
            <Button onClick={handleFinalSubmit} disabled={loading} className="flex-1 h-16 bg-blue-600 text-white font-black rounded-2xl uppercase italic text-xs shadow-xl tracking-widest">REGISTRAR MENSAGEM</Button>
            <Button variant="ghost" onClick={() => setShowConfirmModal(false)} className="h-16 px-10 font-black uppercase text-slate-400 text-xs italic">Sair</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .animate-spin-slow { animation: spin 8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      ` }} />
    </div>
  );
}